import {useState, useCallback, useEffect, useRef} from 'react';
import {Platform} from 'react-native';
import Voice from '@react-native-voice/voice';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {useFocusEffect} from '@react-navigation/native';
import userVoice from '../services/userVoice';
import {
  SpeechErrorEvent,
  SpeechResultsEvent,
} from '@react-native-voice/voice/src/VoiceModuleTypes';
import {requestMicrophonePermission} from '../helpers';

// Define the SpeechPartialResultsEvent type which is similar to SpeechResultsEvent
interface SpeechPartialResultsEvent {
  value?: string[];
}

// Type declaration for the Web Speech API
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionEvent extends Event {
  results: {
    [index: number]: {
      [index: number]: {
        transcript: string;
      };
    };
  };
}

interface SpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
}

// Define the type for the SpeechRecognition constructor
type SpeechRecognitionConstructor = new () => SpeechRecognition;

// Augment the window interface for global access
declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

// Web-specific implementation of speech recognition
const useWebSpeechRecognition = (onResults?: (result: string) => void) => {
  const {language} = useSelector((state: RootState) => state.lang);
  const {languageCode, countryCode} = language || {};
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(
    null,
  );

  useEffect(() => {
    // Initialize SpeechRecognition for Web
    if (
      (typeof window !== 'undefined' && window.SpeechRecognition) ||
      window.webkitSpeechRecognition
    ) {
      const SpeechRecognitionAPI =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (SpeechRecognitionAPI) {
        const recognitionInstance = new SpeechRecognitionAPI();

        // Configure the recognition instance
        recognitionInstance.continuous = false;
        recognitionInstance.interimResults = false;

        // Set up event handlers
        recognitionInstance.onresult = (event: SpeechRecognitionEvent) => {
          const result = event.results[0][0].transcript;
          onResults?.(result);
        };

        recognitionInstance.onerror = (event: SpeechRecognitionErrorEvent) => {
          setIsRecording(false);
        };

        recognitionInstance.onend = () => {
          setIsRecording(false);
        };

        setRecognition(recognitionInstance);
      }
    }

    return () => {
      if (recognition) {
        recognition.abort();
      }
    };
  }, []);

  useEffect(() => {
    // Update language when it changes
    if (recognition && languageCode && countryCode) {
      recognition.lang = `${languageCode}-${countryCode}`;
    } else if (recognition) {
      recognition.lang = 'en-US'; // Default
    }
  }, [languageCode, countryCode, recognition]);

  const startListening = async () => {
    try {
      if (!recognition) {
        throw new Error('Speech recognition not supported in this browser');
      }

      // For web, we should request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      stream.getTracks().forEach(track => track.stop()); // We just needed this to trigger permission prompt

      setIsRecording(true);
      recognition.start();
    } catch (err) {
      setIsRecording(false);
    }
  };

  const stopListening = async () => {
    try {
      if (recognition) {
        recognition.stop();
      }
      setIsRecording(false);
    } catch (err) {
    }
  };

  return {
    isRecording,
    startListening,
    stopListening,
  };
};

// Enhanced Native implementation with continuous listening and partial results accumulation
const useNativeSpeechRecognition = (onResults?: (result: string) => void) => {
  const {language} = useSelector((state: RootState) => state.lang);
  const {languageCode, countryCode} = language || {};
  const [isRecording, setIsRecording] = useState(false);

  // Refs to track the speech recognition state
  const lastPartialResultRef = useRef('');
  const accumulatedTextRef = useRef('');
  const recognitionActiveRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef(0); // Track different recognition sessions

  /**
   * Set up Voice listeners and cleanup
   */
  useFocusEffect(
    useCallback(() => {
      Voice.onSpeechResults = onSpeechResults;
      Voice.onSpeechPartialResults = onSpeechPartialResults;
      Voice.onSpeechEnd = onSpeechEnd;
      Voice.onSpeechError = onSpeechError;

      return () => {
        userVoice?.stop();
        Voice.destroy();
        stopRecognitionCompletely();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []),
  );

  /**
   * Utility function to check if text is already in accumulated text
   * This does a smarter check than just endsWith or includes
   */
  const isTextAlreadyIncluded = (
    newText: string,
    existingText: string,
  ): boolean => {
    if (!existingText) return false;
    if (!newText) return true;

    // Clean and normalize both texts
    const cleanNewText = newText.trim().toLowerCase();
    const cleanExistingText = existingText.trim().toLowerCase();

    // Direct exact match
    if (cleanExistingText === cleanNewText) {
      return true;
    }

    // Check if the text is already included at the end
    if (cleanExistingText.endsWith(cleanNewText)) {
      return true;
    }

    // Check for substring match with longer phrases
    if (cleanNewText.length > 10 && cleanExistingText.includes(cleanNewText)) {
      return true;
    }

    // Check for repetitive pattern - this handles cases where short phrases
    // are repeated with slight variations
    const words = cleanNewText.split(' ');
    if (words.length >= 2) {
      // For multi-word phrases, check if they appear in the last N words
      // of the existing text, which helps catch partial repeats
      const existingWords = cleanExistingText.split(' ');

      // Only check the last part of the existing text (twice the length of new words)
      const lastNWords = existingWords
        .slice(Math.max(0, existingWords.length - words.length * 2))
        .join(' ');

      // If the entire new phrase is in the last portion, consider it a repeat
      if (lastNWords.includes(cleanNewText)) {
        return true;
      }

      // Check for overlapping words at the end
      let matchingWords = 0;
      for (let i = 0; i < Math.min(words.length, 5); i++) {
        const newWord = words[words.length - 1 - i];
        const existingIndex = existingWords.length - 1 - i;

        if (existingIndex >= 0 && existingWords[existingIndex] === newWord) {
          matchingWords++;
        } else {
          break;
        }
      }

      // If more than half of the words (or at least 2) overlap at the end, consider it a repeat
      if (matchingWords >= Math.max(2, Math.floor(words.length / 2))) {
        return true;
      }
    }

    return false;
  };

  /**
   * Utility to remove duplicated phrases from a text string.
   * This addresses the pattern "... and make sure it's lots of and make sure it's lots of dead"
   * where phrases get duplicated after recognition restarts.
   */
  const removeDuplicatedPhrases = (text: string): string => {
    if (!text || text.length < 10) return text;

    const words = text.split(' ');

    // No need to check if we have very few words
    if (words.length < 6) return text;

    // Look for duplicated phrases of varying lengths
    // We'll check for duplicated phrases from 3 to 5 words long
    for (
      let phraseLength = 3;
      phraseLength <= Math.min(10, Math.floor(words.length / 2));
      phraseLength++
    ) {
      // Check sliding windows through the text
      for (let i = 0; i <= words.length - phraseLength * 2; i++) {
        // Create the first phrase
        const firstPhrase = words
          .slice(i, i + phraseLength)
          .join(' ')
          .toLowerCase();

        // Look for this phrase repeated later in the text
        for (let j = i + phraseLength; j <= words.length - phraseLength; j++) {
          const secondPhrase = words
            .slice(j, j + phraseLength)
            .join(' ')
            .toLowerCase();

          // If we found the same phrase and they're close enough to each other (within 10 words)
          if (firstPhrase === secondPhrase && j - (i + phraseLength) < 10) {
            // Create a new array without the second occurrence
            const newWords = [
              ...words.slice(0, j),
              ...words.slice(j + phraseLength),
            ];

            // Recursively clean the resulting text to catch any other duplications
            return removeDuplicatedPhrases(newWords.join(' '));
          }
        }
      }
    }

    return text;
  };

  /**
   * Creates a combined result that intelligently merges accumulated text with new partial results.
   * Helps prevent duplication when rejoining text segments.
   */
  const createCombinedResult = (
    accumulatedText: string,
    newText: string,
  ): string => {
    if (!accumulatedText) return newText;
    if (!newText) return accumulatedText;

    // Clean both texts for proper comparison
    const cleanAccText = accumulatedText.trim();
    const cleanNewText = newText.trim();

    // First, detect if we have a full duplication situation
    // This happens after speech recognition restarts and repeats the entire phrase
    const minLength = Math.min(cleanAccText.length, cleanNewText.length);
    if (minLength > 15) {
      // Check both the beginning and end for substantial overlaps
      // This catches cases where the entire phrase is duplicated

      // Look for the new text appearing in full within the accumulated text
      if (cleanAccText.includes(cleanNewText)) {
        // The new text is entirely contained in the accumulated text, so just keep the accumulated text
        return cleanAccText;
      }

      // Check if accumulated text appears at the start of new text
      // This catches cases where we're restarting with the same phrase
      if (
        cleanNewText.startsWith(
          cleanAccText.substring(0, Math.min(30, cleanAccText.length)),
        )
      ) {
        // The accumulated text is at the start of the new text, so the new text is more complete
        return cleanNewText;
      }
    }

    // Check if the last few words of the accumulated text overlap with the first few words of the new text
    const accWords = cleanAccText.split(' ');
    const newWords = cleanNewText.split(' ');

    // Look for overlapping segments (up to half the length of the new text)
    const maxOverlapCheck = Math.min(Math.floor(newWords.length / 2) + 1, 5);

    for (let overlapSize = maxOverlapCheck; overlapSize > 0; overlapSize--) {
      if (accWords.length < overlapSize) continue;

      // Get the last N words from accumulated text
      const lastSegment = accWords
        .slice(accWords.length - overlapSize)
        .join(' ');
      // Get the first N words from new text
      const firstSegment = newWords.slice(0, overlapSize).join(' ');

      // If they match, we can intelligently join them
      if (lastSegment.toLowerCase() === firstSegment.toLowerCase()) {
        // Return accumulated text plus the remaining portion of new text
        const combined =
          cleanAccText + ' ' + newWords.slice(overlapSize).join(' ');
        // Apply additional duplicate phrase removal
        return removeDuplicatedPhrases(combined);
      }
    }

    // Special handling for common restart pattern:
    // When speech recognition restarts, it often repeats the entire accumulated text
    // plus new content. We need to detect and remove this duplication.
    const accTextLower = cleanAccText.toLowerCase();
    const newTextLower = cleanNewText.toLowerCase();

    // If new text starts with something similar to the full accumulated text
    if (
      newTextLower.includes(
        accTextLower.substring(0, Math.min(accTextLower.length, 30)),
      )
    ) {
      // It's likely a restart with the same content, find where they diverge
      let divergeIndex = newTextLower.indexOf(' ', accTextLower.length);

      // If we found a divergence point, use it
      if (divergeIndex > 0) {
        return cleanNewText;
      }
    }

    // Create a combined text and check for any duplicate phrases
    const combined = cleanAccText + ' ' + cleanNewText;
    return removeDuplicatedPhrases(combined);
  };

  /**
   * Handles partial results from the speech recognition, accumulating them
   * to provide a continuous experience even with pauses.
   */
  const onSpeechPartialResults = (event: SpeechPartialResultsEvent) => {
    if (event?.value && event.value.length > 0) {
      const currentPartial = event.value[0].trim();

      // Only update if we have meaningful content
      if (currentPartial !== '') {
        // Store the last partial result to help determine if we should accumulate
        lastPartialResultRef.current = currentPartial;


        // Check if this partial result is already part of our accumulated text
        if (
          !isTextAlreadyIncluded(currentPartial, accumulatedTextRef.current)
        ) {
          // Create a temporary combined result for real-time feedback using our smart combiner
          const combinedResult = createCombinedResult(
            accumulatedTextRef.current,
            currentPartial,
          );

          // Call the callback with the combined result for real-time updates
          // Apply an additional duplication check as a safeguard
          onResults?.(removeDuplicatedPhrases(combinedResult));

        }
      }
    }
  };

  /**
   * Handles the end of speech detection, managing the accumulation of text
   * and restarting recognition if necessary.
   */
  const onSpeechEnd = () => {
    if (
      lastPartialResultRef.current &&
      lastPartialResultRef.current.trim() !== ''
    ) {
      // Check if the lastPartialResult is already included in accumulated text
      const shouldAdd = !isTextAlreadyIncluded(
        lastPartialResultRef.current,
        accumulatedTextRef.current,
      );

      // Only accumulate text if we have valid partial results and it's not duplicated
      if (shouldAdd) {
        // Use our smart combiner to prevent duplications
        accumulatedTextRef.current = createCombinedResult(
          accumulatedTextRef.current,
          lastPartialResultRef.current,
        );
      }
    }

    // Reset last partial result
    lastPartialResultRef.current = '';

    // Restart recognition after a short delay if we're still supposed to be recording
    if (recognitionActiveRef.current) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Track that we're starting a new session
      const currentSessionId = ++sessionIdRef.current;

      timeoutRef.current = setTimeout(() => {
        // Only proceed if we're still in the same session and recording is active
        if (
          recognitionActiveRef.current &&
          currentSessionId === sessionIdRef.current
        ) {
          startRecognitionEngine();
        }
      }, 300); // Short delay to allow the engine to reset
    }
  };

  /**
   * Handles the event when speech recognition results are obtained.
   * Usually this is fired after onSpeechEnd, but we'll use partial results
   * since they're more reliable with pauses.
   */
  const onSpeechResults = (event: SpeechResultsEvent) => {
    // We primarily rely on partial results and onSpeechEnd for continuous recognition
    // but we'll still process final results if they come in
    if (
      event?.value &&
      event.value.length > 0 &&
      event.value[0].trim() !== ''
    ) {
      const result = event.value[0].trim();

      // Add to accumulated text if it's not already part of our text
      if (!isTextAlreadyIncluded(result, accumulatedTextRef.current)) {
        // Use our smart combiner to prevent duplications
        const combined = createCombinedResult(
          accumulatedTextRef.current,
          result,
        );

        // Apply duplicate phrase removal as an extra safeguard
        accumulatedTextRef.current = removeDuplicatedPhrases(combined);

        // Provide the complete result to the callback
        onResults?.(accumulatedTextRef.current);
      }
    }
  };

  /**
   * Handles errors that occur during the speech recognition process.
   * For certain error types, we'll automatically restart the recognition.
   */
  const onSpeechError = (event: SpeechErrorEvent) => {

    // Handle both "didn't understand" and "service busy" errors
    if (
      (event?.error?.code?.includes("11/Didn't understand") ||
        event?.error?.code?.includes('8/RecognitionService busy')) &&
      recognitionActiveRef.current
    ) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // First destroy and clean up the current recognition instance
      Voice.destroy()
        .then(() => {
          // Then set a timeout to restart after a longer delay (1 second)
          timeoutRef.current = setTimeout(() => {
            if (recognitionActiveRef.current) {
              startRecognitionEngine();
            }
          }, 1000); // Longer delay to ensure the service has time to clean up
        })
        .catch(err => {
          // If destroy fails, still try to restart after a longer delay
          timeoutRef.current = setTimeout(() => {
            if (recognitionActiveRef.current) {
              startRecognitionEngine();
            }
          }, 1500);
        });
    } else {
      // For other errors, we'll stop recording
      setIsRecording(false);
      recognitionActiveRef.current = false;
    }
  };

  /**
   * Starts the actual speech recognition engine with the specified locale and options.
   */
  const startRecognitionEngine = async () => {
    try {
      // Make sure any previous instance is destroyed first
      await Voice.destroy();

      let locale = 'en-US'; // Default locale
      // Custom locale if languageCode and countryCode are set
      if (languageCode && countryCode) {
        locale = `${languageCode}-${countryCode}`;
      }

      // Configure Voice options to increase recognition time
      const options = {
        EXTRA_SPEECH_INPUT_MINIMUM_LENGTH_MILLIS: 60000, // 60 seconds minimum
        EXTRA_SPEECH_INPUT_COMPLETE_SILENCE_LENGTH_MILLIS: 10000, // 10 seconds of silence
        EXTRA_SPEECH_INPUT_POSSIBLY_COMPLETE_SILENCE_LENGTH_MILLIS: 5000, // 5 seconds for possibly complete
        EXTRA_PARTIAL_RESULTS: true, // Ensure we get partial results
      };

      // Set up listeners again after destroy
      Voice.onSpeechResults = onSpeechResults;
      Voice.onSpeechPartialResults = onSpeechPartialResults;
      Voice.onSpeechEnd = onSpeechEnd;
      Voice.onSpeechError = onSpeechError;

      // Start the voice recognition with the determined locale and extended timeout options
      await Voice.start(locale, options);

    } catch (err) {

      // If we fail to start, we'll try again after a longer delay
      if (recognitionActiveRef.current) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          if (recognitionActiveRef.current) {
            startRecognitionEngine();
          }
        }, 2000); // Use a longer delay for retry after an error
      }
    }
  };

  /**
   * Starts listening for voice input from the user.
   * It sets the recording state to true and initiates the voice recognition process.
   */
  const startListening = async () => {
    try {
      const permissionGranted = await requestMicrophonePermission();
      if (!permissionGranted) {
        return;
      }

      // Track a new recording session
      sessionIdRef.current++;

      // Reset accumulated text when starting a new recording session
      accumulatedTextRef.current = '';
      lastPartialResultRef.current = '';

      // Update state
      setIsRecording(true);
      recognitionActiveRef.current = true;

      // Start the recognition engine
      startRecognitionEngine();
    } catch (err) {
      setIsRecording(false);
      recognitionActiveRef.current = false;

    }
  };

  /**
   * Completely stops the speech recognition and cleans up resources.
   */
  const stopRecognitionCompletely = async () => {
    try {
      recognitionActiveRef.current = false;
      // Track a new session on stop to prevent any pending restarts
      sessionIdRef.current++;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      await Voice.stop();
      Voice.removeAllListeners();
      setIsRecording(false);
    } catch (err) {
    }
  };

  /**
   * Stops listening for voice input and cleans up listeners.
   */
  const stopListening = async () => {
    await stopRecognitionCompletely();

    // Provide the final accumulated result one last time
    if (accumulatedTextRef.current) {
      onResults?.(accumulatedTextRef.current);
    }
  };

  return {
    isRecording,
    startListening,
    stopListening,
  };
};

// Platform-specific implementation selection
const useChatVoiceRecognition = (onResults?: (result: string) => void) => {
  // Use platform-specific implementation
  if (Platform.OS === 'web') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useWebSpeechRecognition(onResults);
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useNativeSpeechRecognition(onResults);
  }
};

export default useChatVoiceRecognition;
